/** D1 - clone by default in the adventure kernel.
 *
 * `structuredClone` is the contract, but it is not available in every runtime
 * this code has to run in unmodified (D7), and it is measurably slower than a
 * hand-rolled walk for the plain-data-plus-typed-arrays shape state actually
 * has. So: one well-tested clone function, no copy-on-write machinery.
 */

const TA = [
  Int8Array, Uint8Array, Uint8ClampedArray, Int16Array, Uint16Array,
  Int32Array, Uint32Array, Float32Array, Float64Array,
] as const;

function isTypedArray(v: object): v is Int32Array {
  for (const c of TA) if (v instanceof c) return true;
  return false;
}

export function deepClone<T>(v: T): T {
  if (v === null || typeof v !== "object") return v;
  if (Array.isArray(v)) {
    const out = new Array(v.length);
    for (let i = 0; i < v.length; i++) out[i] = deepClone(v[i]);
    return out as unknown as T;
  }
  if (isTypedArray(v as object)) {
    return (v as unknown as Int32Array).slice() as unknown as T;
  }
  if (v instanceof Map || v instanceof Set || v instanceof Date) {
    throw new Error("Map/Set/Date are not plain data (I6)");
  }
  const src = v as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  // I3 exception: a clone reproduces the source's own key order, so the result
  // is a function of the input alone. Sorting here would cost a pass per command
  // (D1 clones the whole state) and change nothing - the hash canonicalizes keys.
  // eslint-disable-next-line game/deterministic-iteration
  for (const k of Object.keys(src)) out[k] = deepClone(src[k]);
  return out as T;
}
